import React, { useState } from "react";

// Material UI Core Components
import {
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableFooter,
  IconButton,
  Paper,
  Dialog,
  Tooltip,
  Checkbox,
  Grid,
  DialogContent,
} from "@material-ui/core";

// Material UI Icons
import {
  Delete,
  Visibility,
  GetApp,
  RestorePage,
  Add,
} from "@material-ui/icons";
import MDEditor from "@uiw/react-md-editor";

import CloudUploadIcon from "@material-ui/icons/CloudUpload";
import CloseIcon from "@material-ui/icons/Close";

// Material UI Styles
import { makeStyles } from "@material-ui/core/styles";

// Custom Components
import PDFViewer from "views/Components/PDFViewer";
import { quCreateColor } from "assets/jss/material-dashboard-pro-react";
import MarkdownEditorWithPreview from "components/MarkdownEditor/MarkdownEditorWithPreview";

const useStyles = makeStyles((theme) => ({
  container: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    textAlign: "center",
    marginTop: "20px",
    boxSizing: "border-box",
  },
  addButton: {
    backgroundColor: "#3f51b5",
    color: "#fff",
    padding: "10px",
    margin: "10px",
    "&:hover": {
      backgroundColor: "#303f9f",
    },
  },
  title: { marginBottom: "20px" },
  tableContainer: {
    width: "100%",
    maxHeight: "70vh",
    margin: "0 auto",
    borderRadius: "8px",
    boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
    overflow: "hidden",
  },
  dialogContent: {
    width: "500px",
    maxWidth: "90vw",
  },
  dialogButtonSpacing: { margin: "10px 0" },
  pdfViewer: {
    display: "flex",
    justifyContent: "center",
    flexDirection: "column",
    alignItems: "center",
  },
  pdfPage: {
    margin: "10px 0",
  },
  headerCell: {
    backgroundColor: "#20677b",
    color: "#ffffff",
    fontWeight: 600,
    fontSize: "0.9rem",
    padding: "16px",
    borderBottom: "none",
  },
  tableCell: {
    maxWidth: "200px",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    padding: "14px 16px",
    fontSize: "0.875rem",
  },
  tableRow: {
    "&:hover": {
      backgroundColor: "#f5f9fa",
    },
    transition: "background-color 0.2s ease",
  },
  dialogActions: {
    display: "flex",
    justifyContent: "center",
    gap: "10px",
  },
  addResourceRow: {
    textAlign: "center",
    cursor: "pointer",
    backgroundColor: "#f8f9fa",
    borderTop: "2px solid #e0e0e0",
    "&:hover": {
      backgroundColor: "#e8f4f8",
    },
    transition: "background-color 0.2s ease",
  },
  dialogImage: {
    width: "100%",
    maxHeight: "80vh",
    objectFit: "contain",
    borderRadius: "8px",
  },
  emptyStateCell: {
    textAlign: "center",
    padding: "40px 16px",
    color: "#666",
  },
  actionButton: {
    color: "#20677b",
    "&:hover": {
      backgroundColor: "rgba(32, 103, 123, 0.08)",
    },
  },
}));

const getFileNameWithoutExtension = (fileName) =>
  fileName.replace(/\.[^/.]+$/, "");

export default function WritingTable({
  files,
  handleDeleteResource,
  handleReplace,
  handleResourceAddition,
  setSelectedResources,
  selectedResources,
  selectColumnRequired = true,
  viewActionRequired = true,
  atleastOneFileRequired = true,
  uploadActionRequired = true,
  deleteActionRequired = true,
}) {
  // Initialize Material UI styles for the component
  const classes = useStyles();

  // State for controlling the PDF preview modal and the selected PDF file
  const [pdfModalOpen, setPdfModalOpen] = useState(false);
  const [pdfFile, setPdfFile] = useState(null);
  const [markdownFile, setMarkdownFile] = useState(null);
  const [imageModalOpen, setImageModalOpen] = useState(false);
  const [markdownModal, setMarkdownModal] = useState(false);
  const [currentImage, setCurrentImage] = useState(null);

  // Handler to preview a PDF file in a modal dialog
  const handlePreview = async (file) => {
    try {
      if (file.type === "text/markdown") {
        setMarkdownFile(file); // Set the selected file to be previewed

        const formData = new FormData(); // Create a new FormData object
        formData.append("url", file.url);

        fetch(`${process.env.REACT_APP_FASTAPI_URL}/fetch_quizdata`, {
          method: "POST",
          body: formData,
        })
          .then((response) => {
            if (!response.ok) {
              throw new Error("Failed to fetch quiz data");
            }
            return response.json();
          })
          .then((data) => {
            // Do something with the data here if needed
            setMarkdownFile(data); // Set the fetched data to the markdown file state
            setMarkdownModal(true); // Move this inside after response is handled
          })
          .catch((err) => {
            console.error("Error fetching file:", err);
          });
      } else if (file.type === "File") {
        setPdfFile(file); // Set the selected file to be previewed
        setPdfModalOpen(true); // Open the modal containing the PDF viewer
      }
      if (file.type === "Image") {
        setCurrentImage(file.url);
        setImageModalOpen(true);
      }
    } catch (error) {
      // If an error occurs, open the file in a new browser tab as a fallback
      window.open(file.url, "_blank");
    }
  };

  // Handler to download a file by dynamically creating an anchor element
  const handleDownload = (file) => {
    const link = document.createElement("a");
    link.href = file.url; // Set the URL of the file
    link.download = file.name; // Set the filename for download
    link.click(); // Programmatically trigger the download
  };

  // Handler to close the PDF preview modal and reset the selected file
  const handlePdfClose = () => {
    setPdfModalOpen(false); // Close the PDF preview modal
    setPdfFile(null); // Reset the PDF file state
  };

  const handleImagePreviewClose = () => {
    setImageModalOpen(false);
    setCurrentImage(null);
  };

  const handleMarkdownPreviewClose = () => {
    setMarkdownModal(false);
    setMarkdownFile(null);
  };

  return (
    <Grid container>
      <TableContainer component={Paper} className={classes.tableContainer}>
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              {selectColumnRequired && (
                <TableCell className={classes.headerCell} style={{ width: "5%" }}>
                  Select
                </TableCell>
              )}
              <TableCell className={classes.headerCell} style={{ width: "35%" }}>
                File Name
              </TableCell>
              <TableCell className={classes.headerCell} style={{ width: "40%" }}>
                Description
              </TableCell>
              <TableCell className={classes.headerCell} style={{ width: "20%" }}>
                Actions
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {/* Display message when no files are uploaded */}
            {files.length === 0 && (
              <TableRow>
                <TableCell
                  colSpan={selectColumnRequired ? 4 : 3}
                  className={classes.emptyStateCell}
                >
                  <CloudUploadIcon style={{ fontSize: 48, color: "#ccc", marginBottom: 8 }} />
                  <Typography variant="body1" color="textSecondary">
                    No files uploaded yet
                  </Typography>
                  <Typography variant="body2" color="textSecondary" style={{ marginTop: 4 }}>
                    Click the upload button below to add files
                  </Typography>
                </TableCell>
              </TableRow>
            )}

            {/* Render files if they exist */}
            {files.map((file, index) => (
              <TableRow key={index} className={classes.tableRow}>
                {selectColumnRequired && (
                  <TableCell
                    className={classes.tableCell}
                    style={{ width: "5%" }}
                  >
                    <Checkbox
                      type="checkbox"
                      checked={selectedResources
                        .map((r) => r.id)
                        .includes(file.id)}
                      onChange={() => {
                        if (
                          selectedResources.map((r) => r.id).includes(file.id)
                        ) {
                          setSelectedResources(
                            selectedResources.filter((r) => r.id !== file.id)
                          );
                        } else {
                          setSelectedResources([...selectedResources, file]);
                        }
                      }}
                      color="quCreateColor"
                    />
                  </TableCell>
                )}
                <TableCell
                  className={classes.tableCell}
                  style={{ width: "40%" }}
                >
                  <Tooltip
                    title={file.name}
                    disableHoverListener={file.name.length < 20}
                  >
                    <div>
                      <span>{getFileNameWithoutExtension(file.name)}</span>
                    </div>
                  </Tooltip>
                </TableCell>
                <TableCell
                  className={classes.tableCell}
                  style={{ width: "40%" }}
                >
                  <Tooltip
                    title={file.description}
                    disableHoverListener={file.description.length < 20}
                  >
                    <div>
                      <span>{file.description}</span>
                    </div>
                  </Tooltip>
                </TableCell>
                <TableCell className={classes.tableCell} style={{ width: "20%" }}>
                  {viewActionRequired && (
                    <Tooltip title="Preview">
                      <span>
                        <IconButton
                          className={classes.actionButton}
                          size="small"
                          onClick={() => handlePreview(file)}
                          disabled={
                            !(
                              file.type === "File" ||
                              file.type === "Image" ||
                              file.type === "text/markdown"
                            )
                          }
                        >
                          <Visibility fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  )}
                  <Tooltip title="Download">
                    <IconButton
                      className={classes.actionButton}
                      size="small"
                      onClick={() => handleDownload(file)}
                    >
                      <GetApp fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip
                    title={
                      files.length === 1
                        ? "At least one file is required"
                        : "Delete"
                    }
                  >
                    <span>
                      <IconButton
                        className={classes.actionButton}
                        size="small"
                        onClick={() => handleDeleteResource(file, index)}
                        disabled={files.length === 1}
                        style={{
                          color: files.length === 1 ? undefined : "#d32f2f",
                        }}
                      >
                        <Delete fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
          {/* Sticky Add Resource Row */}
          {uploadActionRequired && (
            <TableFooter>
              <TableRow
                style={{ position: "sticky", bottom: 0, zIndex: 2 }}
                className={classes.addResourceRow}
              >
                <TableCell colSpan={selectColumnRequired ? 4 : 3} style={{ textAlign: "center", padding: "16px" }}>
                  <IconButton
                    color="primary"
                    size="medium"
                    style={{
                      backgroundColor: quCreateColor,
                      borderRadius: "8px",
                      color: "white",
                      padding: "10px 24px",
                      "&:hover": {
                        backgroundColor: "#1a5563",
                      },
                    }}
                    onClick={handleResourceAddition}
                  >
                    <Add style={{ marginRight: 8 }} />
                    <Typography variant="body2" style={{ fontWeight: 500 }}>
                      Upload Files
                    </Typography>
                  </IconButton>
                </TableCell>
              </TableRow>
            </TableFooter>
          )}
        </Table>
      </TableContainer>

      {/* PDF Preview Modal */}
      <Dialog
        open={pdfModalOpen}
        onClose={handlePdfClose}
        maxWidth="md"
        fullWidth
      >
        {pdfFile && (
          <PDFViewer
            pdfUrl={pdfFile.url}
            scrollView={false}
            showButtons={true}
          />
        )}
      </Dialog>

      {/* Dialog for Image Preview */}
      <Dialog
        open={imageModalOpen}
        onClose={handleImagePreviewClose}
        maxWidth="md"
        fullWidth
      >
        <DialogContent>
          {currentImage && (
            <img
              src={currentImage}
              alt="Preview"
              className={classes.dialogImage}
            />
          )}
        </DialogContent>
      </Dialog>
      {/* Dialog for Markdown Preview */}
      <Dialog
        open={markdownModal}
        onClose={handleMarkdownPreviewClose}
        fullWidth
        maxWidth="lg"
      >
        <DialogContent>
          <div data-color-mode="light">
            {markdownFile && (
              <MarkdownEditorWithPreview
                value={markdownFile.markdown}
                height={400}
                onChange={() => {}}
                disabled={true}
              />
            )}
          </div>
        </DialogContent>
      </Dialog>
    </Grid>
  );
}
